import { Game, getSchedule, Schedule } from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";

import { isDeletedSchedule } from "@/utils/scheduleUtils";

function getDayWindow(date: Date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(date);
  end.setHours(23, 59, 59, 0);
  const nowS = Math.trunc(Date.now() / 1000);
  return {
    startTime: Math.max(Math.trunc(start.getTime() / 1000), nowS),
    endTime: Math.trunc(end.getTime() / 1000),
  };
}

function timeToMs(date: Date, time: string): number {
  const [h, m] = time.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

function getGameFromSchedule(schedule: Schedule): Game | null {
  return schedule.merged?.game ?? null;
}

export const useTimeSlotAvailability = ({
  fieldId,
  startDate,
  startTime,
  endTime,
  excludeScheduleId,
}: {
  fieldId?: string;
  startDate?: Date;
  startTime?: string;
  endTime?: string;
  excludeScheduleId?: string;
}) => {
  const enabled = !!fieldId && !!startDate && !!startTime && !!endTime;

  const { data, isPending, isError } = useQuery({
    queryKey: [
      "time-slot-availability",
      fieldId,
      startDate?.toDateString(),
      startTime,
      endTime,
      excludeScheduleId,
    ],
    enabled,
    queryFn: async () => {
      const { startTime: dayStart, endTime: dayEnd } = getDayWindow(startDate!);

      const { data } = await getSchedule({
        query: {
          machineUuid: fieldId!,
          startTime: dayStart,
          endTime: dayEnd,
          count: 1000,
        },
        throwOnError: true,
      });

      const selectedStart = timeToMs(startDate!, startTime!);
      const selectedEnd = timeToMs(startDate!, endTime!);

      return (data?.schedules ?? [])
        .filter((s) => !isDeletedSchedule(s))
        .filter((s) => !excludeScheduleId || s.id !== excludeScheduleId)
        .filter((s) => {
          const game = getGameFromSchedule(s);
          if (!game?.times?.length) return false;

          const firstTime = game.times[0];
          const lastTime = game.times.at(-1);

          const slotStart = ((firstTime?.startTime ?? 0) as number) * 1000;
          const slotEnd =
            (((lastTime?.endTime ?? firstTime?.startTime ?? 0) as number) ??
              0) * 1000;

          return slotStart < selectedEnd && slotEnd > selectedStart;
        })
        .flatMap((s) => {
          const game = getGameFromSchedule(s);
          return game ? [game] : [];
        });
    },
  });

  return { events: data ?? [], isPending, isError };
};
